/**
 * Utility functions for handling product images
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export interface ProductImage {
  id: number;
  image_url: string;
  is_primary?: boolean;
  product_id?: number;
  thumbnail_url?: string;
  storage_type?: string;
}

/**
 * Get a placeholder image URL
 * Uses a reliable placeholder service
 */
export const getPlaceholderImage = (width: number = 400, height: number = 400, text: string = 'No Image'): string => {
  // Use a data URI SVG as fallback instead of external service to avoid network issues
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
    <rect fill="#e5e7eb" width="100%" height="100%"/>
    <text fill="#9ca3af" font-family="sans-serif" font-size="18" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">${text}</text>
  </svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/**
 * Get the full URL for an image
 * Handles both relative paths and full URLs
 */
export const getImageUrl = (imagePath: string | ProductImage | undefined | null): string => {
  if (!imagePath) {
    return getPlaceholderImage();
  }

  // If it's already an object with image_url
  if (typeof imagePath === 'object' && 'image_url' in imagePath) {
    const url = imagePath.image_url;
    if (!url || url.trim() === '') return getPlaceholderImage();
    // If it's already a full URL, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // If it starts with /api/v1/images, prepend the base URL
    if (url.startsWith('/api/v1/images')) {
      return `${API_BASE_URL.replace('/api/v1', '')}${url}`;
    }
    // If it starts with /uploads, it's a legacy path
    if (url.startsWith('/uploads')) {
      return `${API_BASE_URL.replace('/api/v1', '')}${url}`;
    }
    // Otherwise, assume it's a relative path (legacy)
    return `${API_BASE_URL.replace('/api/v1', '')}/uploads${url.startsWith('/') ? '' : '/'}${url}`;
  }

  // If it's a string
  if (typeof imagePath === 'string') {
    if (!imagePath || imagePath.trim() === '') return getPlaceholderImage();
    
    // Check if it's a malformed placeholder URL (e.g., "400x400?text=No%20Image")
    if (/^\d+x\d+(\?text=.*)?$/.test(imagePath)) {
      // Extract dimensions and text if present
      const match = imagePath.match(/^(\d+)x(\d+)(\?text=(.*))?$/);
      if (match) {
        const width = parseInt(match[1]);
        const height = parseInt(match[2]);
        const text = match[4] ? decodeURIComponent(match[4]) : 'No Image';
        return getPlaceholderImage(width, height, text);
      }
    }
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // If it starts with /api/v1/images, prepend the base URL
    if (imagePath.startsWith('/api/v1/images')) {
      return `${API_BASE_URL.replace('/api/v1', '')}${imagePath}`;
    }
    // If it starts with /uploads, it's a legacy path
    if (imagePath.startsWith('/uploads')) {
      return `${API_BASE_URL.replace('/api/v1', '')}${imagePath}`;
    }
    // Otherwise, assume it's a relative path (legacy)
    return `${API_BASE_URL.replace('/api/v1', '')}/uploads${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
  }

  return getPlaceholderImage();
};

/**
 * Get the primary image from a product's images array
 * Returns the first image if available, otherwise returns a placeholder
 */
export const getPrimaryImage = (images: ProductImage[] | string[] | any | undefined | null): string => {
  // Handle null, undefined, or empty
  if (!images) {
    return getPlaceholderImage();
  }

  // If it's a string, try to parse it as JSON (backend might return JSON string)
  if (typeof images === 'string') {
    try {
      const parsed = JSON.parse(images);
      if (Array.isArray(parsed) && parsed.length > 0) {
        images = parsed;
      } else {
        // If parsed but not array, treat as single image URL
        return getImageUrl(images);
      }
    } catch (e) {
      // If parsing fails, treat as single image URL string
      return getImageUrl(images);
    }
  }

  // If it's an object but not an array, treat as single image object
  if (typeof images === 'object' && !Array.isArray(images)) {
    if ('image_url' in images) {
      return getImageUrl(images);
    }
    return getPlaceholderImage();
  }

  // Ensure it's an array at this point
  if (!Array.isArray(images) || images.length === 0) {
    return getPlaceholderImage();
  }

  // Get first image
  const firstImage = images[0];

  // If first item is an object with image_url
  if (typeof firstImage === 'object' && firstImage !== null && 'image_url' in firstImage) {
    const imageObj = firstImage as ProductImage;
    // Check if image_url is valid
    if (!imageObj.image_url || imageObj.image_url.trim() === '') {
      return getPlaceholderImage();
    }
    return getImageUrl(imageObj);
  }

  // If first item is a string
  if (typeof firstImage === 'string') {
    const imageStr = firstImage;
    // Check if string is valid
    if (!imageStr || imageStr.trim() === '') {
      return getPlaceholderImage();
    }
    return getImageUrl(imageStr);
  }

  return getPlaceholderImage();
};

/**
 * Get all image URLs from a product's images array
 */
export const getAllImageUrls = (images: ProductImage[] | string[] | any | undefined | null): string[] => {
  // Handle null, undefined, or empty
  if (!images) {
    return [getPlaceholderImage()];
  }

  // If it's a string, try to parse it as JSON (backend might return JSON string)
  if (typeof images === 'string') {
    try {
      const parsed = JSON.parse(images);
      if (Array.isArray(parsed)) {
        images = parsed;
      } else {
        return [getPlaceholderImage()];
      }
    } catch (e) {
      // If parsing fails, treat as single image URL string
      return [getImageUrl(images)];
    }
  }

  // If it's an object but not an array, try to convert it
  if (typeof images === 'object' && !Array.isArray(images)) {
    // If it has image_url property, treat as single image object
    if ('image_url' in images) {
      return [getImageUrl(images)];
    }
    // Otherwise, return placeholder
    return [getPlaceholderImage()];
  }

  // Ensure it's an array at this point
  if (!Array.isArray(images)) {
    return [getPlaceholderImage()];
  }

  // Handle empty array
  if (images.length === 0) {
    return [getPlaceholderImage()];
  }

  // Map images to URLs
  const urls = images.map(img => getImageUrl(img));
  
  // If all URLs are placeholders, return just one
  if (urls.every(url => url.includes('data:image/svg+xml') || url.includes('placeholder'))) {
    return [getPlaceholderImage()];
  }
  
  return urls;
};

